import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest, ClientResponse, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, FormBuilderKeyValue, mapResultToIdNameCount } from 'app/shared/components/form-builder';
import { CheckListField, IdNameArrayComboValueHandlers, EntityUtils, SearchSelectField, IdNameComboValueHandlers, ComboField } from '../utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    ratingProfile?: IdName;
    assetStateProfiles?: IdName[];
}

export class AssetProfilesSectionHandler implements FormSectionHandler<AssetResponse, EditorValues> {

    type = 'form';
    icon = 'badge-check';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    properties = ['ratingProfile', 'assetStateProfiles', 'roadProfile', 'geoLockProfile', 'privacyProfile'];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public client: ClientResponse,
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.PROFILES.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetResponse): Promise<EditorValues> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, entity, {}));
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: EditorValues): Promise<AssetUpdateRequest> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    async getFields(entity: AssetResponse): Promise<FormBuilderField[]> {

        const features = this.admin.getFeaturesForClient({ flags: this.client?.meta?.mergedFlags || {} });
        const hasPrivacy = features?.privacy?.trip_privacy?.enabled === true;

        const [geoLockListResponse, roadListResponse, privacyListResponse] = await Promise.all([
            this.admin.getCached(`listGeoLockProfiles/${this.ownerId}/0/100/name/recurse=true&state=active`, api => api.entities.listGeoLockProfiles(this.ownerId, true, 0, 100, 'name', 'state=active'), 15).catch(nullAccessDenied),
            this.admin.getCached(`listRoadProfiles/${this.ownerId}/0/100/name/recurse=true&state=active`, api => api.entities.listRoadProfiles(this.ownerId, true, 0, 100, 'name', 'state=active'), 15).catch(nullAccessDenied),
            hasPrivacy && this.admin.getCached(`listPrivacyProfiles/${this.ownerId}/0/100/name/recurse=true&state=active`, api => api.entities.listPrivacyProfiles(this.ownerId, true, 0, 100, 'name', 'state=active'), 15).catch(nullAccessDenied),
        ]);

        const roadProfiles = roadListResponse ? roadListResponse.items.sort((a, b) => a.name.localeCompare(b.name)) : [];
        const roadProfileFields: FormBuilderKeyValue[] = [
            { key: '00000000-0000-0000-0000-000000000000', value: this.admin.i18n.instant('SHARED.NONE') },
            ...roadProfiles.map(x => ({ key: x.id, value: x.name, })),
        ];

        const geoLockProfiles = geoLockListResponse ? geoLockListResponse.items.sort((a, b) => a.name.localeCompare(b.name)) : [];
        const geoLockProfileFields: FormBuilderKeyValue[] = [
            { key: '00000000-0000-0000-0000-000000000000', value: this.admin.i18n.instant('SHARED.NONE') },
            ...geoLockProfiles.map(x => ({ key: x.id, value: x.name, })),
        ];

        const privacyProfiles = privacyListResponse ? privacyListResponse.items.sort((a, b) => a.name.localeCompare(b.name)) : [];
        const privacyProfileFields: FormBuilderKeyValue[] = [
            { key: '00000000-0000-0000-0000-000000000000', value: this.admin.i18n.instant('SHARED.NONE') },
            ...privacyProfiles.map(x => ({ key: x.id, value: x.name, })),
        ];

        return this.admin.getCached(`listAssetStateProfiles/${this.ownerId}`, api => api.entities.listAssetStateProfiles(this.ownerId), 15).then(stateProfiles => {
            return [
                SearchSelectField('ratingProfile', this.translate('FIELDS.ASSET_RATING'), false, {
                    searchListPromise: (limit, filter) => {
                        filter = 'state=active' + (!!filter ? `&name=*${filter}*` : '');
                        return this.admin.getCached(`listAssetRatingProfiles/${this.ownerId}/${filter}`, api => api.entities.listAssetRatingProfiles(this.ownerId, 0, limit, 'name:asc', filter).then(mapResultToIdNameCount));
                    },
                    searchItemPromise: id => this.admin.getCached(`listAssetRatingProfiles/${id}`, api => api.entities.getAssetRatingProfile(id)).then(x => ({ id: x.id, name: x.name })),
                }),
                {
                    ...CheckListField('assetStateProfiles', this.translate('FIELDS.ASSET_STATE'), false, EntityUtils.idNameToComboValues(stateProfiles.items.filter(x => x.state === 'active')), IdNameArrayComboValueHandlers), options: {
                        checkListPromise: stateProfiles.count > stateProfiles.limit ? async filter => {
                            const filteredProfiles = await this.admin.getCached(`listAssetStateProfiles/${this.ownerId}/${filter}`, api => api.entities.listAssetStateProfiles(this.ownerId, 0, 50, 'name:asc', 'state=active' + (!!filter ? `&name=*${filter}*` : '')));
                            return EntityUtils.idNameToComboValues(filteredProfiles.items);
                        } : null,
                    },

                },
                ComboField('roadProfile', this.translate('FIELDS.ROAD_PROFILE'), true, roadProfileFields, IdNameComboValueHandlers),
                ComboField('geoLockProfile', this.translate('FIELDS.GEO_LOCK_PROFILE'), true, geoLockProfileFields, IdNameComboValueHandlers),
                hasPrivacy && ComboField('privacyProfile', this.translate('FIELDS.PRIVACY_PROFILE'), true, privacyProfileFields, IdNameComboValueHandlers),
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, this.properties, fields));
    }
}
